import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { userApi, trainingApi, templateApi } from '../../api'
import type { ApiError } from '../../api/client'
import type { DayOfWeek, TrainDay, TrainingSetCreateRequest, UserRelation, TrainingProjection, TrainingSet, TrainingSetTemplate } from '../../types/api'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useSubscription } from '../../hooks/useSubscription'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { TRAINING_SET_STATUS } from '../../constants'
import { parseDuration } from '../../utils/duration'
import { getDayAbbreviation, formatDurationTranslated } from '../../utils/dateTranslations'
import { Button, Card, DashedPlaceholder } from '../ui'
import { TrainingProjectionRow } from './TrainingProjectionRow'
import { captureEvent } from '../../posthog'

export default function CreateTrainingSet() {
  const navigate = useNavigate()
  const { id: traineeId } = useParams<{ id: string }>()
  const theme = useTheme()
  const { t } = useTranslation()
  const { usage, maxTrainingSetDurationDays } = useSubscription()
  useUserPreferences()

  const DAYS_OF_WEEK: { value: DayOfWeek; abbr: string; label: string }[] = [
    { value: 'MONDAY',    abbr: t.common.dayAbbr.mon, label: t.common.day.monday },
    { value: 'TUESDAY',   abbr: t.common.dayAbbr.tue, label: t.common.day.tuesday },
    { value: 'WEDNESDAY', abbr: t.common.dayAbbr.wed, label: t.common.day.wednesday },
    { value: 'THURSDAY',  abbr: t.common.dayAbbr.thu, label: t.common.day.thursday },
    { value: 'FRIDAY',    abbr: t.common.dayAbbr.fri, label: t.common.day.friday },
    { value: 'SATURDAY',  abbr: t.common.dayAbbr.sat, label: t.common.day.saturday },
    { value: 'SUNDAY',    abbr: t.common.dayAbbr.sun, label: t.common.day.sunday },
  ]

  const [trainee, setTrainee] = useState<UserRelation | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState<TrainingProjection[]>([])
  const [existingSets, setExistingSets] = useState<TrainingSet[]>([])
  const [hasOverlap, setHasOverlap] = useState(false)
  const [durationExceeded, setDurationExceeded] = useState<{ current: number; max: number } | null>(null)
  const [templates, setTemplates] = useState<TrainingSetTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)

  const getDefaultTime = () => {
    const now = new Date()
    const minutes = now.getMinutes()
    const roundedMinutes = minutes < 30 ? 30 : 0
    const hour = minutes < 30 ? now.getHours() : now.getHours() + 1
    return `${hour.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`
  }

  const [selectedDays, setSelectedDays] = useState<Map<DayOfWeek, string>>(new Map())
  const [startDate, setStartDate] = useState('')
  const [plannedTrainings, setPlannedTrainings] = useState<number | ''>(12)
  const [durationHours, setDurationHours] = useState<number | ''>(1)
  const [durationMinutes, setDurationMinutes] = useState<number | ''>(30)
  const [defaultStartTime, setDefaultStartTime] = useState(getDefaultTime())
  const [maxSkipCount, setMaxSkipCount] = useState<number | ''>('')
  const errorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!error) return
    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [error])

  useEffect(() => {
    loadTraineeData()
    loadTemplates()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traineeId])

  async function loadTemplates() {
    try {
      const data = await templateApi.getTemplates()
      setTemplates(data)
    } catch (err) {
      console.error('Error loading templates:', err)
    }
  }

  async function loadTraineeData() {
    if (!traineeId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const trainees = await userApi.getTrainees()
      const foundTrainee = trainees.find(t => t.id === parseInt(traineeId))
      if (foundTrainee) setTrainee(foundTrainee)

      const sets = await trainingApi.getTraineeSets(parseInt(traineeId))
      setExistingSets(sets)

      const hasActiveSet = sets.some(set => set.status === TRAINING_SET_STATUS.ACTIVE)
      if (hasActiveSet) {
        alert(t.createTrainingSet.errors.hasActiveSet)
        navigate(`/trainee/${traineeId}`)
        return
      }
    } catch (err) {
      console.error('Error loading trainee:', err)
    } finally {
      setLoading(false)
    }
  }

  const checkOverlap = (newStartDate: string, newEndDate: string): boolean => {
    const newStart = new Date(newStartDate)
    const newEnd = new Date(newEndDate)
    return existingSets.some(set => {
      const existingStart = new Date(set.startDate)
      const existingEnd = new Date(set.calculatedEndDate)
      return newStart <= existingEnd && newEnd >= existingStart
    })
  }

  const applyTemplate = (templateId: number) => {
    const template = templates.find(t => t.id === templateId)
    if (!template) return

    const templateDefaultTime = template.defaultStartTime
      ? template.defaultStartTime.slice(0, 5)
      : defaultStartTime

    if (template.days && template.days.length > 0) {
      const newDays = new Map<DayOfWeek, string>()
      template.days.forEach(day => {
        newDays.set(day.day, day.startTime ? day.startTime.slice(0, 5) : templateDefaultTime)
      })
      setSelectedDays(newDays)
    }
    if (template.defaultStartTime) setDefaultStartTime(templateDefaultTime)
    if (template.plannedTrainings) setPlannedTrainings(template.plannedTrainings)
    if (template.duration) {
      const { hours, minutes } = parseDuration(template.duration)
      setDurationHours(hours)
      setDurationMinutes(minutes)
    }
    if (template.maxSkipCount !== undefined) setMaxSkipCount(template.maxSkipCount)

    setSelectedTemplateId(templateId)
  }

  function handleDayToggle(day: DayOfWeek) {
    setError(null)
    setSelectedDays(prev => {
      const next = new Map(prev)
      if (next.has(day)) {
        next.delete(day)
      } else {
        next.set(day, defaultStartTime)
      }
      return next
    })
  }

  function handleDayTimeChange(day: DayOfWeek, time: string) {
    setSelectedDays(prev => new Map(prev).set(day, time))
  }

  const buildRequest = (): TrainingSetCreateRequest | null => {
    if (selectedDays.size === 0) {
      setError(t.createTrainingSet.errors.noDaysSelected)
      return null
    }
    if (!startDate) {
      setError(t.createTrainingSet.errors.startDateRequired)
      return null
    }
    if (!traineeId) return null

    const days: TrainDay[] = Array.from(selectedDays.entries()).map(([day, time]) => ({
      day,
      startTime: time || defaultStartTime,
    }))
    const duration = `PT${durationHours || 0}H${durationMinutes || 0}M`
    return {
      traineeId: parseInt(traineeId),
      days,
      startDate,
      plannedTrainings: plannedTrainings !== '' ? plannedTrainings : 12,
      duration,
      maxSkipCount: maxSkipCount !== '' ? maxSkipCount : undefined,
    }
  }

  const handlePreview = async () => {
    setError(null)
    const request = buildRequest()
    if (!request) return

    if (usage && usage.traineeCount > usage.maxTrainees) {
      setError(t.createTrainingSet.errors.traineeLimit || 'Trainee limit exceeded')
      return
    }

    setSubmitting(true)
    try {
      const preview = await trainingApi.getTrainingPreview(request)
      setPreviewData(preview)

      if (preview.length > 0 && startDate) {
        const lastTraining = preview[preview.length - 1].end
        const endDate = new Date(lastTraining)
        const start = new Date(startDate)
        const durationDays = Math.ceil((endDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
        setDurationExceeded(durationDays > maxTrainingSetDurationDays ? { current: durationDays, max: maxTrainingSetDurationDays } : null)
        setHasOverlap(checkOverlap(startDate, lastTraining))
      }
      setShowPreview(true)
    } catch (err) {
      console.error('Error fetching preview:', err)
      const apiError = err as ApiError
      setError(apiError?.message || t.createTrainingSet.errors.previewFailed)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreate = async () => {
    const request = buildRequest()
    if (!request) return

    setSubmitting(true)
    try {
      await trainingApi.createTrainingSet(request)
      captureEvent('training_set_created', { trainee_id: request.traineeId })
      alert(t.createTrainingSet.messages.createSuccess)
      navigate(`/trainee/${traineeId}`)
    } catch (err) {
      console.error('Error creating training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.createTrainingSet.errors.createFailed)
    } finally {
      setSubmitting(false)
    }
  }

  const sectionLabel = {
    fontSize: '11px' as const,
    fontWeight: 700 as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.text.muted,
    opacity: 0.7,
    marginBottom: '10px',
  }
  const rowBorder = { borderBottom: `1px solid ${theme.cardBorder}` }
  const activeDays = DAYS_OF_WEEK.filter(d => selectedDays.has(d.value))
  const traineeName = trainee
    ? (trainee.firstName ? `${trainee.firstName} ${trainee.lastName || ''}`.trim() : trainee.username || '')
    : ''

  const warningBanner = (title: string, body: string, action?: { label: string; onClick: () => void }) => (
    <div
      className="rounded-xl p-4 mb-4 flex items-start gap-3"
      style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <div>
        <p className="text-sm font-semibold mb-0.5" style={{ color: '#ef4444' }}>{title}</p>
        <p className="text-xs" style={{ color: '#ef4444', opacity: 0.85 }}>{body}</p>
        {action && (
          <button
            type="button"
            className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' }}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  )

  if (showPreview && previewData.length > 0) {
    const firstTraining = previewData[0]
    const firstStart = new Date(firstTraining.start)
    const firstWeekStart = new Date(firstStart)
    firstWeekStart.setHours(0, 0, 0, 0)
    const grouped = previewData.reduce((acc, training, index) => {
      const trainingDate = new Date(training.start)
      const daysDiff = Math.floor((trainingDate.getTime() - firstWeekStart.getTime()) / (1000 * 60 * 60 * 24))
      const weekNumber = Math.floor(daysDiff / 7) + 1
      if (!acc[weekNumber]) acc[weekNumber] = []
      acc[weekNumber].push({ training, index })
      return acc
    }, {} as Record<number, Array<{ training: TrainingProjection; index: number }>>)

    return (
      <div className="p-5 pb-24">
        <header className="mb-6">
          <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.createTrainingSet.preview.title}
          </h1>
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.createTrainingSet.preview.subtitle.replace('{{count}}', String(previewData.length))}
          </p>
        </header>

        {durationExceeded && warningBanner(
          t.createTrainingSet.preview.durationExceeded,
          t.createTrainingSet.errors.durationLimit
            .replace('{current}', durationExceeded.current.toString())
            .replace('{max}', durationExceeded.max.toString()),
          { label: t.subscription.unlockCta, onClick: () => navigate('/subscription') }
        )}
        {hasOverlap && warningBanner(
          t.createTrainingSet.preview.overlapWarning.title,
          t.createTrainingSet.preview.overlapWarning.description,
        )}

        <div className="flex flex-col gap-5 mb-6">
          {Object.entries(grouped).map(([weekNum, trainings]) => (
            <div key={weekNum}>
              <p style={{ ...sectionLabel, marginBottom: '8px' }}>
                {t.createTrainingSet.preview.week} {weekNum}
              </p>
              <Card className="p-0 overflow-hidden">
                {trainings.map(({ training, index }, i) => (
                  <TrainingProjectionRow
                    key={index}
                    training={training}
                    displayNumber={index + 1}
                    showBorderBottom={i < trainings.length - 1}
                    showStatuses={false}
                  />
                ))}
              </Card>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="success"
            size="lg"
            fullWidth
            disabled={submitting || hasOverlap || !!durationExceeded}
            onClick={handleCreate}
          >
            {submitting
              ? t.createTrainingSet.button.creating
              : (hasOverlap || durationExceeded)
                ? t.createTrainingSet.button.cannotCreate
                : t.createTrainingSet.button.create}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            disabled={submitting}
            onClick={() => setShowPreview(false)}
          >
            {t.createTrainingSet.button.backToEdit}
          </Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="p-5 text-center pt-16" style={{ color: theme.text.muted }}>
        {t.common.loading}
      </div>
    )
  }

  return (
    <div className="p-5 pb-24">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.createTrainingSet.title}
        </h1>
        {traineeName && (
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.createTrainingSet.subtitle.replace('{{trainee}}', traineeName)}
          </p>
        )}
      </header>

      {/* TEMPLATE */}
      {templates.length > 0 && (
        <>
          <p style={sectionLabel}>{t.createTrainingSet.useTemplate}</p>
          <Card className="p-0 overflow-hidden mb-6">
            <div className="overflow-y-auto" style={{ maxHeight: '280px' }}>
            {templates.map((template, idx) => {
              const isSelected = selectedTemplateId === template.id
              return (
                <button
                  key={template.id}
                  type="button"
                  className="w-full text-left px-4 py-3 flex items-center justify-between transition-colors"
                  style={{
                    ...(idx < templates.length - 1 ? rowBorder : {}),
                    backgroundColor: isSelected ? `${theme.buttonColor}10` : 'transparent',
                  }}
                  onClick={() => applyTemplate(template.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: theme.text.primary }}>
                      {template.name}
                    </p>
                    {template.description && (
                      <p className="text-xs mt-0.5 truncate" style={{ color: theme.text.muted }}>
                        {template.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {template.plannedTrainings && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }}
                        >
                          {template.plannedTrainings} {t.createTrainingSet.templateCard.trainings}
                        </span>
                      )}
                      {template.duration && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }}
                        >
                          {formatDurationTranslated(parseDuration(template.duration), t)}
                        </span>
                      )}
                      {template.days && template.days.length > 0 && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }}
                        >
                          {template.days.map(d => getDayAbbreviation(d.day, t)).join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-3 shrink-0" style={{ color: theme.accent.text }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              )
            })}
            </div>
          </Card>
        </>
      )}

      {/* SCHEDULE */}
      <p style={sectionLabel}>{t.templateForm.sections.schedule}</p>

      <Card className="p-0 overflow-hidden mb-4">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-3">
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </span>
            <div>
              <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                {t.createTrainingSet.form.defaultStartTime}
              </p>
              <p className="text-[11px]" style={{ color: theme.text.muted }}>
                {t.createTrainingSet.form.defaultStartTimeHelp}
              </p>
            </div>
          </div>
          <input
            type="time"
            value={defaultStartTime}
            onChange={e => setDefaultStartTime(e.target.value)}
            className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
            style={{ color: theme.text.primary }}
          />
        </div>
      </Card>

      <div className="flex justify-between gap-1 mb-3">
        {DAYS_OF_WEEK.map(({ value, abbr }) => {
          const active = selectedDays.has(value)
          return (
            <button
              key={value}
              type="button"
              onClick={() => handleDayToggle(value)}
              className="flex-1 flex flex-col items-center gap-1 py-1 select-none"
            >
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold transition-all active:scale-90"
                style={active ? {
                  backgroundColor: theme.buttonColor,
                  color: theme.buttonTextColor,
                  boxShadow: `0 4px 12px ${theme.buttonColor}40`,
                } : {
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                  color: theme.text.muted,
                }}
              >
                {abbr.charAt(0)}
              </span>
              <span
                className="text-[10px] font-medium"
                style={{ color: active ? theme.accent.text : theme.text.muted, opacity: active ? 1 : 0.5 }}
              >
                {abbr}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{
            backgroundColor: selectedDays.size > 0 ? `${theme.buttonColor}18` : theme.cardBg,
            border: `1px solid ${selectedDays.size > 0 ? `${theme.buttonColor}40` : theme.cardBorder}`,
            color: selectedDays.size > 0 ? theme.accent.text : theme.text.muted,
          }}
        >
          {selectedDays.size} {t.templateForm.daysPerWeek}
        </span>
        {selectedDays.size > 0 && (
          <span className="text-xs" style={{ color: theme.text.muted }}>
            {t.templateForm.tapToAdjustTime}
          </span>
        )}
      </div>

      {activeDays.length > 0 ? (
        <Card className="p-0 overflow-hidden mb-6">
          {activeDays.map((day, idx) => (
            <div
              key={day.value}
              className="flex items-center justify-between px-4 py-3.5"
              style={idx < activeDays.length - 1 ? rowBorder : undefined}
            >
              <div className="flex items-center gap-3">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
                >
                  {day.abbr.charAt(0)}
                </span>
                <span className="text-sm font-medium" style={{ color: theme.text.primary }}>
                  {day.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted, opacity: 0.6 }}>
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <input
                  type="time"
                  value={selectedDays.get(day.value) || ''}
                  onChange={e => handleDayTimeChange(day.value, e.target.value)}
                  className="text-sm font-semibold bg-transparent border-none outline-none"
                  style={{ color: theme.text.primary }}
                />
              </div>
            </div>
          ))}
        </Card>
      ) : (
        <DashedPlaceholder
          className="mb-6"
          title={t.templateForm.noTrainingDays.title}
          hint={t.templateForm.noTrainingDays.hint}
        />
      )}

      {/* START DATE */}
      <p style={sectionLabel}>{t.createTrainingSet.form.startDate}</p>
      <Card className="p-0 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-3">
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </span>
            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
              {t.createTrainingSet.form.startDate} *
            </p>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value)
              setError(null)
            }}
            className="text-sm font-semibold bg-transparent border-none outline-none shrink-0"
            style={{ color: theme.text.primary }}
            required
          />
        </div>
      </Card>

      {/* GOALS */}
      <p style={sectionLabel}>{t.templateForm.sections.goals}</p>
      <Card className="p-0 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-4 py-3.5" style={rowBorder}>
          <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
            {t.createTrainingSet.form.plannedTrainings}
          </p>
          <input
            type="number"
            value={plannedTrainings}
            onChange={e => setPlannedTrainings(e.target.value ? parseInt(e.target.value) : '')}
            placeholder="—"
            className="w-20 text-sm font-semibold bg-transparent border-none outline-none text-right"
            style={{ color: theme.text.primary }}
            min={1}
            max={365}
          />
        </div>
        <div className="flex items-center justify-between px-4 py-3.5">
          <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
            {t.createTrainingSet.form.sessionDuration}
          </p>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={durationHours}
              onChange={e => setDurationHours(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="0"
              className="w-10 text-sm font-semibold bg-transparent border-none outline-none text-right"
              style={{ color: theme.text.primary }}
              min={0}
              max={12}
            />
            <span className="text-xs" style={{ color: theme.text.muted }}>{t.templateForm.durationUnits.hours}</span>
            <input
              type="number"
              value={durationMinutes}
              onChange={e => setDurationMinutes(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="0"
              className="w-10 text-sm font-semibold bg-transparent border-none outline-none text-right"
              style={{ color: theme.text.primary }}
              min={0}
              max={59}
            />
            <span className="text-xs" style={{ color: theme.text.muted }}>{t.templateForm.durationUnits.minutes}</span>
          </div>
        </div>
      </Card>

      {/* SKIP RULES */}
      <p style={sectionLabel}>{t.templateForm.sections.skipRules}</p>
      <Card className="p-0 overflow-hidden mb-8">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="flex-1 mr-4">
            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
              {t.createTrainingSet.form.maxSkipCount}
            </p>
            <p className="text-[11px]" style={{ color: theme.text.muted }}>
              {t.createTrainingSet.form.maxSkipCountHint}
            </p>
          </div>
          <input
            type="number"
            value={maxSkipCount}
            onChange={e => {
              const v = e.target.value
              if (v === '') {
                setMaxSkipCount('')
              } else {
                const n = parseInt(v, 10)
                if (!isNaN(n)) setMaxSkipCount(n)
              }
            }}
            placeholder="—"
            className="w-20 text-sm font-semibold bg-transparent border-none outline-none text-right"
            style={{ color: theme.text.primary }}
            min={0}
            max={365}
          />
        </div>
      </Card>

      {error && (
        <div
          ref={errorRef}
          className="rounded-xl p-4 mb-4 flex items-start gap-3"
          style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
        </div>
      )}

      <Button
        type="button"
        variant="primary"
        size="lg"
        fullWidth
        disabled={submitting}
        onClick={handlePreview}
      >
        {submitting ? t.createTrainingSet.preview.loadingPreview : t.createTrainingSet.button.preview}
      </Button>
    </div>
  )
}
